/**
 * CustomerAdminChatView - Customer view for admin support messages
 * Allows customers to chat with support team
 */

import { useState, useEffect, useRef } from 'react'
import { Send, ArrowLeft, Loader2, Paperclip, X } from 'lucide-react'
import AttachmentPreview from './AttachmentPreview'
import { ATTACHMENT_ACCEPT, validateAttachment, visibleMessageText } from '../../lib/messages/attachments.js'
import { useCustomerAdminMessages } from '../../hooks/useCustomerAdminMessages'
import { useAuth } from '../../contexts/AuthContext'
import { format } from 'date-fns'

interface CustomerAdminChatViewProps {
  onBack: () => void
}

export default function CustomerAdminChatView({ onBack }: CustomerAdminChatViewProps) {
  const { messages, isLoading, sendMessage, markAllAsRead } = useCustomerAdminMessages()
  const { user } = useAuth()
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Mark messages as read when component mounts
  useEffect(() => {
    markAllAsRead()
  }, [markAllAsRead])

  const handleSend = async () => {
    if ((!newMessage.trim() && !file) || isSending) return

    setIsSending(true)
    setSendError(null)
    const result = await sendMessage(newMessage, file)

    if (result.success) {
      setNewMessage('')
      setFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } else {
      console.error('Failed to send message:', result.error)
      setSendError(result.error || 'Could not send')
    }

    setIsSending(false)
  }

  const handlePickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] || null
    if (!picked) return
    const check = validateAttachment(picked)
    if (!check.ok) {
      setSendError(check.error || 'That file cannot be attached')
      e.target.value = ''
      return
    }
    setSendError(null)
    setFile(picked)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white">
        <button
          onClick={onBack}
          className="hover:bg-white/20 rounded-full p-1 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h3 className="font-semibold">Support Team</h3>
          <p className="text-xs text-blue-100">We&apos;re here to help</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No messages yet</p>
            <p className="text-xs mt-1">Send a message to start chatting with our support team</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwnMessage = message.sender_id === user?.id
            const messageTime = format(new Date(message.created_at), 'MMM d, h:mm a')

            return (
              <div
                key={message.id}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-lg px-4 py-2 ${
                    isOwnMessage
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {!isOwnMessage && (
                    <div className="text-xs font-medium mb-1 opacity-75">
                      Support
                    </div>
                  )}
                  {message.attachment_url && (
                    <div className="mb-2">
                      <AttachmentPreview url={message.attachment_url} type={message.attachment_type || ''} name={message.attachment_name || 'attachment'} isOwnMessage={isOwnMessage} />
                    </div>
                  )}
                  {visibleMessageText(message) && (
                    <p className="text-sm whitespace-pre-wrap break-words">
                      {visibleMessageText(message)}
                    </p>
                  )}
                  <div
                    className={`text-xs mt-1 ${
                      isOwnMessage ? 'text-white/70' : 'text-gray-500'
                    }`}
                  >
                    {messageTime}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-4 bg-white">
        {file && (
          <div className="mb-2 flex items-center gap-2 text-xs text-gray-600">
            <Paperclip className="w-3.5 h-3.5" />
            <span className="truncate">{file.name}</span>
            <button type="button" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = '' }} className="text-gray-400 hover:text-gray-600" aria-label="Remove attachment"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {sendError && <p className="mb-2 text-xs text-red-600">{sendError}</p>}
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} onChange={handlePickFile} className="hidden" aria-label="Attach a file" />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending}
            className="border rounded-lg px-3 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            title="Attach a photo or PDF (10 MB)"
            aria-label="Attach a file"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 resize-none border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            rows={1}
            disabled={isSending}
          />
          <button
            onClick={handleSend}
            disabled={(!newMessage.trim() && !file) || isSending}
            className="bg-blue-600 text-white rounded-lg px-4 py-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
